import React, { useState } from 'react';
import { supabase } from './supabase';

const S = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #0a0c0b;
    --surface: #111410;
    --card: #161a15;
    --border: #252a25;
    --text: #e8ede8;
    --muted: #6a7a6a;
    --gold: #c9a84c;
    --gold2: #e8c96a;
    --green: #4caf7d;
    --red: #e05c5c;
    --sans: 'DM Sans', sans-serif;
    --serif: 'Playfair Display', serif;
    --mono: 'DM Mono', monospace;
  }

  body {
    background: var(--bg);
    color: var(--text);
    font-family: var(--sans);
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .auth-wrap {
    width: 100%;
    max-width: 420px;
    padding: 24px;
  }

  .auth-logo {
    text-align: center;
    margin-bottom: 40px;
  }

  .auth-logo-mark {
    font-size: 32px;
    color: var(--gold);
    margin-bottom: 8px;
  }

  .auth-logo-name {
    font-family: var(--serif);
    font-size: 28px;
    font-weight: 800;
    color: var(--text);
    letter-spacing: -0.5px;
  }

  .auth-logo-sub {
    font-size: 11px;
    color: var(--muted);
    letter-spacing: 3px;
    text-transform: uppercase;
    margin-top: 4px;
  }

  .auth-card {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 32px;
  }

  .auth-tabs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;
    background: var(--surface);
    border-radius: 10px;
    padding: 4px;
    margin-bottom: 28px;
  }

  .auth-tab {
    padding: 9px;
    border: none;
    border-radius: 8px;
    background: none;
    color: var(--muted);
    font-family: var(--sans);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all .15s;
  }

  .auth-tab.active {
    background: var(--card);
    color: var(--gold);
    border: 1px solid var(--border);
  }

  .auth-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: var(--muted);
    letter-spacing: .08em;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  .auth-input {
    width: 100%;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 11px 14px;
    color: var(--text);
    font-family: var(--sans);
    font-size: 14px;
    outline: none;
    transition: border .15s;
    margin-bottom: 16px;
  }

  .auth-input:focus { border-color: var(--gold); }

  .auth-btn {
    width: 100%;
    background: var(--gold);
    color: #1a1400;
    border: none;
    border-radius: 8px;
    padding: 13px;
    font-family: var(--sans);
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: background .15s;
    margin-top: 4px;
  }

  .auth-btn:hover { background: var(--gold2); }
  .auth-btn:disabled { opacity: .6; cursor: not-allowed; }

  .auth-error {
    background: rgba(224,92,92,.1);
    border: 1px solid rgba(224,92,92,.25);
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 13px;
    color: var(--red);
    margin-bottom: 16px;
  }

  .auth-success {
    background: rgba(76,175,125,.1);
    border: 1px solid rgba(76,175,125,.25);
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 13px;
    color: var(--green);
    margin-bottom: 16px;
  }

  .auth-footer {
    text-align: center;
    margin-top: 24px;
    font-size: 12px;
    color: var(--muted);
  }

  .auth-footer-links {
    text-align: center;
    margin-top: 10px;
    font-size: 11px;
    color: var(--muted);
  }

  .auth-footer-links a {
    color: var(--muted);
    text-decoration: none;
    transition: color .15s;
  }

  .auth-footer-links a:hover {
    color: var(--text);
  }
`;

export default function Auth({ onAuth }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        onAuth(data.user);
      } else {
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        if (data.user && !data.user.confirmed_at) {
          setSuccess('Account created! Check your email to confirm your account, then log in.');
          setMode('login');
        } else {
          onAuth(data.user);
        }
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <>
      <style>{S}</style>
      <div className="auth-wrap">
        <div className="auth-logo">
          <div className="auth-logo-mark">⬡</div>
          <div className="auth-logo-name">NumiSync</div>
          <div className="auth-logo-sub">Coin Dealer Platform</div>
        </div>

        <div className="auth-card">
          <div className="auth-tabs">
            <button className={`auth-tab ${mode === 'login' ? 'active' : ''}`} onClick={() => { setMode('login'); setError(''); setSuccess(''); }}>
              Sign In
            </button>
            <button className={`auth-tab ${mode === 'signup' ? 'active' : ''}`} onClick={() => { setMode('signup'); setError(''); setSuccess(''); }}>
              Create Account
            </button>
          </div>

          {error && <div className="auth-error">{error}</div>}
          {success && <div className="auth-success">{success}</div>}

          <form onSubmit={handleSubmit}>
            <label className="auth-label">Email</label>
            <input
              className="auth-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus
            />

            <label className="auth-label">Password</label>
            <input
              className="auth-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={mode === 'signup' ? 'Minimum 6 characters' : '••••••••'}
              required
              minLength={6}
            />

            <button className="auth-btn" type="submit" disabled={loading}>
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          </form>
        </div>

        <div className="auth-footer">
          Early access beta · <a href="https://numisync.app" style={{ color: 'var(--gold)', textDecoration: 'none' }}>numisync.app</a>
        </div>
        <div className="auth-footer-links">
          <a href="https://numisync.app/privacy.html" target="_blank" rel="noreferrer">Privacy Policy</a>
          {' · '}
          <a href="https://numisync.app/terms.html" target="_blank" rel="noreferrer">Terms of Service</a>
          {' · '}
          <a href="https://numisync.app/security.html" target="_blank" rel="noreferrer">Security</a>
        </div>
      </div>
    </>
  );
}